import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { ITabItem } from './common';
import { buildClassNames } from '@ui/utils/classNameUtils';
import ScrollBar, { ScrollBarInstance } from '../../../components/ScrollBar/ScrollBar';

import styles from './tabManager.module.scss';
import TabManagerItem from './TabManagerItem';

export type TabManagerSize = 'large'|'default'|'small';

export type TabManagerType = 'default'|'inline'|'button'|'button-rounded';

export interface IProps {
    activeTabId?: string;
    type?: TabManagerType;
    size?: TabManagerSize;
    /**
     * Размер иконки, если не определен - берется значение из size
     */
    iconSize?: TabManagerSize;
    wide?: boolean;
    /** @deprecated use @see type = 'inline' */
    inline?: boolean;
    withBorder?: boolean;
    withDots?: boolean;
    /** @deprecated this style will be removed later */
    light?: boolean;
    nowrap?: boolean;
    noMargin?: boolean;
    children: any;
    actions?: JSX.Element|React.ReactNode;
    reverseTabs?: boolean;
    /**
     * отключение скролла табов
     */
    disableScroll?: boolean;

    className?: any;

    onChange?: (tabId: string) => boolean|void;
}

function getType(props: IProps): TabManagerType {
    if (props.type) {
        return props.type;
    }

    if (props.inline) {
        return 'inline';
    }

    return 'default';
}

const TabManager: React.FC<IProps> = props => {
    const {
        activeTabId,
        children,
        onChange,
        className,
        reverseTabs,
        disableScroll,
    } = props;
    const type = getType(props);
    const isInline = type === 'inline';
    const [ maxWidth, setMaxWidth ] = useState<number|undefined>(undefined);

    const scrollInstanceRef = useRef<ScrollBarInstance>();
    const tabItemsRef = useRef<HTMLDivElement>();

    const [ currentTabId, setCurrentTabId ] = React.useState(activeTabId);
    const selectedTabId = typeof props.onChange === 'function'
        ? activeTabId
        : currentTabId;

    useEffect(() => {
        if (typeof props.onChange !== 'function') {
            setCurrentTabId(activeTabId);
        }
    }, [ activeTabId, onChange]);

    useLayoutEffect(() => {
        if (type === 'button-rounded' || !scrollInstanceRef.current || !tabItemsRef.current) {
            setMaxWidth(undefined);
            return;
        }

        const scrollInstance = scrollInstanceRef.current;
        const tabItems = tabItemsRef.current;

        if (scrollInstance.scrollWidth <= scrollInstance.clientWidth) {
            setMaxWidth(undefined);
            return;
        }

        const buttons = tabItems.querySelectorAll<HTMLButtonElement>('button');
        let maxWidth = 0;
        buttons.forEach(button => {
            maxWidth = Math.max(maxWidth, button.clientWidth);
        });

        if (maxWidth > 0) {
            setMaxWidth(maxWidth);
        }
    }, [ props.children, type ]);


    const didTabItemClicked = useCallback(itemId => {
        const result = onChange && onChange(itemId);

        if (!result) {
            setCurrentTabId(itemId);
        }

        if (!scrollInstanceRef.current || !tabItemsRef.current) {
            return;
        }

        const scrollInstance = scrollInstanceRef.current;
        const tabItems = tabItemsRef.current;

        const activeButton = tabItems.querySelector<HTMLButtonElement>(`[data-tab-id="${itemId}"]`);
        if (!activeButton) {
            return;
        }

        const thumbWidth = maxWidth || 0;
        const x = activeButton.offsetLeft / (scrollInstance.scrollWidth - thumbWidth) * (scrollInstance.scrollWidth - scrollInstance.clientWidth);
        scrollInstance.scrollLeft = x;
    }, [ setCurrentTabId, onChange, maxWidth ]);

    const items: ITabItem[] = React.Children.map(children, tabContainer => {
        if (!tabContainer) {
            return null;
        }

        return tabContainer.props as ITabItem;
    }).filter(item => item != null);

    const activeChildContainer = ((React.Children.toArray(children) as any) as React.Component<ITabItem>[])
        .find(c => c.props.id === selectedTabId);

    const containerClassName = buildClassNames(styles, [
        'container',
        `container--type-${type}`,
    ]) + (className ? ` ${className}` : '');

    const tabItemsClassName = buildClassNames(styles, [
        'tab-list',
        `tab-list--type-${type}`,
        `tab-list--size-${props.size}`,
        !props.noMargin && 'container--margin',
        !isInline && props.withDots && 'tab-list--dots',
        !isInline && props.withBorder && 'tab-list--border',
        props.nowrap && 'tab-items--nowrap',
    ]);

    const getTabItemClassName = item => buildClassNames(styles, [
        'tab-item',
        `tab-item--type-${type}`,
        `tab-item--size-${props.size}`,
        props.wide && 'tab-item--wide',
        !isInline && props.light && 'tab-item--light',
        !isInline && props.withDots && 'tab-item--dots',
        !isInline && props.withBorder && 'tab-item--border',
        (item && item.id) === selectedTabId ? 'tab-item--active' : null,
        item?.disabled && 'tab-item--disabled',
    ]);

    const iconClassName = [
        'tab-item__icon',
        `tab-icon--size-${props.iconSize ?? props.size}`,
    ];

    const leftIconClassName = buildClassNames(styles, [
        ...iconClassName,
        'tab-item__icon--left',
    ]);

    const rightIconClassName = buildClassNames(styles, [
        ...iconClassName,
        'tab-item__icon--right',
    ]);

    return (
        <div className={containerClassName}>
            <ScrollBar
                ref={scrollInstanceRef as any}
                noScrollY
                translateContentSizeYToHolder
                maximalThumbXSize={maxWidth}
                disable={['button', 'button-rounded'].includes(type) || disableScroll}
                visible
            >
                <div className="d-flex d-flex--v-align-center">
                    <div ref={tabItemsRef as any} className={tabItemsClassName}>
                        {(reverseTabs ? items.reverse() : items).map(item => {

                            const didButtonClick = (e: React.MouseEvent) => {
                                if (!item?.disabled) {
                                    if (item.onClick) {
                                        item.onClick(e);
                                    } else {
                                        didTabItemClicked(item.id);
                                    }
                                }
                            };

                            return (
                                <TabManagerItem
                                    id={item.id}
                                    title={item.title}
                                    hint={item.hint}
                                    key={item.id}
                                    onClick={didButtonClick}
                                    onDelete={item?.onDelete}
                                    className={getTabItemClassName(item)}
                                    leftIcon={item.leftIcon}
                                    rightIcon={item.rightIcon}
                                    activeLeftIcon={item.activeLeftIcon}
                                    activeRightIcon={item.activeRightIcon}
                                    leftIconClassName={leftIconClassName}
                                    rightIconClassName={rightIconClassName}
                                    maxWidth={maxWidth}
                                    deletable={item.deletable}
                                    disabled={item.disabled}
                                    badge={item.badge}
                                    isSelected={item.id === selectedTabId}
                                />
                            );
                        })}
                    </div>
                    {props.actions && (
                        <div className={styles['actions']}>
                            {props.actions}
                        </div>
                    )}
                </div>
            </ScrollBar>

            {activeChildContainer as any}
        </div>
    );
};

export default TabManager;